export const readRequiredJson = async <T>(
  response: Pick<Response, 'ok' | 'status' | 'json'>,
  isValid: (value: unknown) => value is T,
  label: string,
): Promise<T> => {
  if (!response.ok) throw new Error(`${label} returned HTTP ${response.status}`);
  const value: unknown = await response.json();
  if (!isValid(value)) throw new Error(`${label} returned an invalid response`);
  return value;
};

export const checkServiceHealth = async (
  request: () => Promise<Pick<Response, 'ok'>>,
): Promise<'active' | 'inactive'> => {
  try {
    return (await request()).ok ? 'active' : 'inactive';
  } catch {
    return 'inactive';
  }
};

type OperationsCostRate = {
  provider?: unknown;
  cost_today?: unknown;
  cost_7d?: unknown;
  cost_30d?: unknown;
};

const finiteNumber = (value: unknown): number => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

export const awsCostSummaryFromOperations = (value: { rates?: OperationsCostRate[] }) => {
  const rates = (value.rates || []).filter(rate => String(rate.provider || '').startsWith('AWS'));
  const byService = new Map<string, number>();
  rates.forEach(rate => {
    const service = String(rate.provider);
    byService.set(service, (byService.get(service) || 0) + finiteNumber(rate.cost_today));
  });
  const services = Array.from(byService, ([service, usd]) => ({ service, usd }));
  return {
    today: {
      total_usd: services.reduce((sum, service) => sum + service.usd, 0),
      services,
    },
    week: { total_usd: rates.reduce((sum, rate) => sum + finiteNumber(rate.cost_7d), 0) },
    month: { total_usd: rates.reduce((sum, rate) => sum + finiteNumber(rate.cost_30d), 0) },
    allocation: { events_today: 0, cost_per_event_usd: 0, cost_per_billable_usd: 0 },
  };
};
